import { useEffect, useState } from 'react';
import { useV2 } from './ctx';
import { jt, jtS, todayIso, shortDate, amtOf, SALDO_TOL } from './model';

// One list of "Perlu tindakan" items, shown the same way on Ringkasan and
// Buku Kas › Daftar (TindakanList below). Director, 3 Oct: alerts may live in both
// places, but they must be the same items in the same style. The old Daftar
// banners said the same thing as Ringkasan in different words and colours.
// Order: what makes the numbers wrong first, then decisions, then info.
// Each item has a `key`; whoever renders it decides what clicking does
// (Ringkasan navigates, Daftar filters in place).
const OPS_URL = 'https://skl-ops.ptmercusuar.com';

export function useTindakan() {
  const { model, match, matchOcbc, data, acc, api, role, planId, refreshKey, piut, fc, trucks } = useV2();
  const [sn, setSn] = useState(null);
  useEffect(() => { let live = true; if (planId) api.overdueSnoozes(planId).then(r => live && setSn(r)).catch(() => live && setSn({ snoozed: [], maxDays: 3 })); return () => { live = false; }; }, [api, planId, refreshKey]);
  if (!model) return { rows: [], late: null };
  const today = todayIso();
  const bm = match?.available ? match : null;
  const rows = [];
  // with OCBC in use, the BCA items say BCA (beside "Selisih OCBC" a bare "Selisih bank" is ambiguous)
  const bcaW = acc?.available && acc.ocbc?.now ? ' BCA' : '';
  const add = r => rows.push(r);

  // 1. saldo awal: vs last month in the app, and vs the bank's close
  const prev = data?.prevMonth, monthGap = prev ? model.saldoAwal - prev.actualEnd : 0;
  const bankGap = acc?.available ? acc.bank.start - model.saldoAwal : 0;
  // small gaps in rupiah: "+0,00 jt" said nothing
  const gapS = g => (Math.abs(g) < 100000 ? `${g > 0 ? '+' : '−'}Rp ${Math.abs(Math.round(g)).toLocaleString('id-ID')}` : `${jtS(g)} jt`);
  if (Math.abs(monthGap) >= SALDO_TOL || Math.abs(bankGap) >= SALDO_TOL) {
    const prevName = prev ? new Date(prev.monthKey + '-01T00:00:00').toLocaleDateString('id-ID', { month: 'long' }) : 'bulan lalu';
    const bits = [Math.abs(monthGap) >= SALDO_TOL && `akhir ${prevName} di app ${jt(prev.actualEnd)} jt (beda ${gapS(monthGap)})`, Math.abs(bankGap) >= SALDO_TOL && `bank ${jt(acc.bank.start)} jt (beda ${gapS(bankGap)})`].filter(Boolean);
    add({ key: 'sa', tone: 'red', label: 'Saldo awal tidak cocok', detail: Math.abs(monthGap) >= SALDO_TOL ? `${gapS(monthGap)} vs akhir ${prevName}` : `${gapS(bankGap)} vs bank`, full: `Saldo awal ${jt(model.saldoAwal)} jt · ${bits.join(' · ')} · semua proyeksi ikut bergeser`, canFix: Math.abs(monthGap) >= SALDO_TOL });
  }
  // 1b. the forecast saldo goes below zero (Director, 20 Oct, rolling plan):
  //     the plan as it stands plus the trips the trucks will really make
  //     (services/forecast.js), before any loan from the Director.
  if (fc?.available && fc.saldo?.firstNeg) {
    const lo = fc.saldo.low;
    // BCA is what pays; if OCBC holds money, moving it is the first answer (30 Oct)
    const ocbcNow = acc?.available ? acc.ocbc?.now || 0 : 0;
    add({ key: 'fcneg', tone: 'red', label: `Saldo BCA di bawah nol mulai ${shortDate(fc.saldo.firstNeg)}`, detail: `perkiraan · terendah ${jtS(lo.saldo)} jt pada ${shortDate(lo.date)} · ${ocbcNow > 0 ? `OCBC ada ${jt(ocbcNow)} jt, pindahkan?` : `perlu pinjaman ±${jt(-lo.saldo)} jt`}`, full: 'Plan sekarang ditambah perjalanan yang belum ada di plan (dari posisi truk), faktur Accurate yang belum di plan, sebelum pinjaman Direktur', day: fc.saldo.firstNeg });
  }
  // 1c. trucks (stage 2A, Director 21 Oct: "go with ops as home"): Arus Kas
  //     only asks when it changes the money: trucks in the workshop with no
  //     return date, so the forecast is guessing. One line; it opens the
  //     first truck's "Bengkel & rencana" in the Ops app, where the date goes.
  const noDate = (trucks || []).filter(t => t.needsDate);
  if (noDate.length) {
    add({ key: 'tdate', tone: 'amber', href: `${OPS_URL}/#maint-dashboard?r=${noDate[0].ticket.id}&from=aruskas`,
      label: noDate.length === 1 ? `${noDate[0].no} di bengkel, belum ada tanggal kembali` : `${noDate.length} truk di bengkel tanpa tanggal kembali`,
      detail: `${noDate.length > 1 ? noDate.map(t => t.no).join(', ') + ' · ' : ''}perkiraan memakai lama perbaikan biasa` });
  }
  // 2. overdue plan lines
  const all = model.overdueLines(bm, today);
  const snoozed = new Set(sn?.snoozed || []), max = sn?.maxDays ?? 3;
  if (all.length) {
    const must = all.filter(o => o.late > max).length, zz = all.filter(o => snoozed.has(o.line.id)).length;
    const days = [...new Set(all.map(o => shortDate(model.confirmedDate(o.line))))];
    add({ key: 'late', tone: must ? 'red' : 'amber', label: `${all.length} terlambat`,
      detail: [`${jt(all.reduce((t, o) => t + amtOf(o.line), 0))} jt`, `rencana ${days.slice(0, 2).join(', ')}${days.length > 2 ? '…' : ''}, belum dibayar`, must && `${must} perlu keputusan`, zz && `${zz} dibiarkan hari ini`].filter(Boolean).join(' · '),
      full: 'Rencana sudah lewat, belum dibayar, belum ada di bank' });
  }
  // 3. waiting for the Director
  const nPend = model.items.length;
  if (nPend) add({ key: 'pend', tone: 'amber', label: `${nPend} menunggu${role === 'director' ? ' keputusan' : ' Direktur'}`, detail: `saldo akhir ${jt(model.settledEnd)} → ${jt(model.tentativeEnd)} jt (${jtS(model.tentativeEnd - model.settledEnd)})` });
  // 4. bank vs app, line by line
  if (bm) {
    // "Sudah benar" on a line's bank note (key line:<id>) takes it off the list.
    const ok = new Set(bm.dismissed || []);
    const v = Object.entries(bm.lines).filter(([id]) => !ok.has('line:' + id)).map(([, x]) => x);
    const toRecord = v.filter(x => ['bank_paid', 'bank_paid_diff'].includes(x.state)).length;
    const differ = v.filter(x => ['date', 'amount', 'sign', 'app_only'].includes(x.state)).length;
    // a paid line whose truck differs from Accurate's text (bankMatch.js › truck)
    const truck = Object.entries(bm.lines).filter(([id, x]) => x.truck && model.lineById[id]?.status === 'disbursed' && !ok.has('truck:' + id)).length;
    const only = bm.bankOnly.length, n = toRecord + differ + only + truck;
    if (n) add({ key: 'bank', tone: 'amber', label: `${n} cek bank${bcaW}`, detail: [truck && `${truck} truk beda`, toRecord && `${toRecord} sudah di bank, belum dicatat`, differ && `${differ} beda tanggal/jumlah`, only && `${only} hanya di bank`].filter(Boolean).join(' · ') });
  }
  // 4o. the same for OCBC (PT AJT), its entries against its own lines
  //     (Director, 30 Oct: the 28 Okt OCBC money sat in the ledger with
  //     nothing on this list pointing at it).
  if (matchOcbc?.available) {
    const ok = new Set(matchOcbc.dismissed || []);
    const v = Object.entries(matchOcbc.lines).filter(([id]) => !ok.has('line:' + id)).map(([, x]) => x);
    const toRecord = v.filter(x => ['bank_paid', 'bank_paid_diff'].includes(x.state)).length;
    const differ = v.filter(x => ['date', 'amount', 'sign', 'app_only'].includes(x.state)).length;
    const only = (matchOcbc.bankOnly || []).length, n = toRecord + differ + only;
    if (n) add({ key: 'bankocbc', tone: 'amber', label: `${n} cek bank OCBC`, detail: [toRecord && `${toRecord} sudah di bank, belum dicatat`, differ && `${differ} beda tanggal/jumlah`, only && `${only} hanya di bank (${jt((matchOcbc.bankOnly || []).reduce((t, e) => t + Math.abs(e.amount), 0))} jt)`].filter(Boolean).join(' · ') });
  }
  // 4b. plan receipts vs Accurate invoices (services/piutang.js)
  if (piut?.available) {
    const un = piut.unplanned, nd = piut.amountDiffs.filter(id => model.lineById[id]).length, lt = piut.late.length;
    const n = un.length + nd + lt;
    if (n) add({ key: 'piut', tone: 'amber', label: `${n} cek piutang`, detail: [un.length && `${un.length} faktur belum di plan (${jt(un.reduce((t, i) => t + i.owing, 0))} jt)`, nd && `${nd} jumlah beda dengan faktur`, lt && `${lt} terlambat dari kebiasaan`].filter(Boolean).join(' · ') });
  }
  // 5. app saldo vs bank, on CLOSED days only (Director, 15 Oct: the item
  //    said +74,68 jt but its link opened a 3 jt day). Today isn't closed:
  //    Accurate's balance is live while its entries only sync at night, and
  //    today's planned payments haven't gone out yet. The item is the gap on
  //    the last closed day; it links to the day that gap began (the start of
  //    the current run of non-matching days), not to an old, resolved one.
  if (acc?.available) {
    const daily = acc.bank.daily;
    const closed = model.dates.map((iso, i) => ({ iso, i })).filter(({ iso }) => iso < today && daily[iso] != null);
    const gapAt = ({ iso, i }) => daily[iso] - model.settled[i];
    const last = closed[closed.length - 1];
    if (last && Math.abs(gapAt(last)) >= 10) {
      let k = closed.length - 1;
      while (k > 0 && Math.abs(gapAt(closed[k - 1])) >= 10) k--;
      const since = closed[k].iso;
      add({ key: 'gapday', tone: 'amber', label: `Selisih${bcaW || ' bank'} ${jtS(gapAt(last))} jt`, detail: since === last.iso ? `per ${shortDate(last.iso)}` : `sejak ${shortDate(since)} · per ${shortDate(last.iso)}`, day: since });
    }
  }
  // 5o. OCBC's saldo vs its bank, closed days, same rule as 5
  if (acc?.available && acc.ocbc?.daily) {
    const od = acc.ocbc.daily, start = data.plan?.saldoAwalOcbc || 0;
    const days = data.arusKas?.daily || [];
    const closed = days.map((d, i) => ({ iso: d.date, i })).filter(({ iso }) => iso < today && od[iso] != null);
    const gapAt = ({ iso, i }) => od[iso] - (days[i].saldoOcbcConfirmed ?? start);
    const last = closed[closed.length - 1];
    if (last && Math.abs(gapAt(last)) >= 10) {
      let k = closed.length - 1;
      while (k > 0 && Math.abs(gapAt(closed[k - 1])) >= 10) k--;
      const since = closed[k].iso;
      add({ key: 'gapocbc', tone: 'amber', label: `Selisih OCBC ${jtS(gapAt(last))} jt`, detail: since === last.iso ? `per ${shortDate(last.iso)}` : `sejak ${shortDate(since)} · per ${shortDate(last.iso)}`, day: since });
    }
  }
  // 6. GPS
  if (model.gpsIssues.length) {
    const f = model.gpsIssues.filter(l => model.gpsOf(l).state === 'flag').length;
    const twice = model.gpsIssues.filter(l => model.gpsOf(l).taken).length, n = model.gpsIssues.length - f - twice;
    add({ key: 'gps', tone: 'amber', label: `${model.gpsIssues.length} cek GPS`, detail: [twice && `${twice} kemungkinan dibayar 2x (1 trip GPS, 2 pembayaran)`, n && `${n} tanpa trip GPS yang cocok`, f && `${f} dibayar tanpa truk`].filter(Boolean).join(' · ') });
  }
  // 7. info: recorded by the machine
  const recent = model.autoLines.filter(l => model.autoOf(l).undoable);
  if (recent.length) add({ key: 'auto', tone: 'green', label: `${recent.length} otomatis`, detail: `${jt(recent.reduce((t, l) => t + amtOf(l), 0))} jt dicatat dari bank · 7 hari · bisa dibatalkan per baris` });

  return { rows, late: { all, snoozed, max, setSn } };
}

// The list itself, identical on Ringkasan and Buku Kas (Director, 3 Oct:
// "prominent but not disturbingly so"; of three mockups he picked this one
// because lines read as things to act on, tiles/bars read as numbers that
// are just there). One quiet line per item: dot · bold label · grey detail ·
// a text link. No filled buttons. `linkOf(row)` = link text (null = none),
// `on` = the row whose filter/dropdown is open, `below(row)` = what opens under it.
export function TindakanList({ rows, linkOf, onPick, on, below }) {
  if (!rows.length) return null;
  return (
    <div className="todo-list" role="list" aria-label="Perlu tindakan">
      {rows.map(r => {
        const link = linkOf(r);
        return (
          <div key={r.key} role="listitem">
            <div className={`todo-l ${r.tone} ${on === r.key ? 'on' : ''} ${link ? 'tap' : ''}`} title={r.full || undefined} onClick={link ? () => onPick(r) : undefined}>
              <span className="dot" /><b>{r.label}</b><span className="s">{r.detail}</span>{link && <span className="go">{link}</span>}
            </div>
            {below && below(r)}
          </div>
        );
      })}
    </div>
  );
}
